'use client'

import { ChangeEvent, useRef } from 'react'
import { Button, Icon } from '@vinted/web-ui'
import { Camera24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { AcceptedFileTypes, MAXIMUM_FILE_SIZE_BYTES } from '@marketplace-web/shared/media-upload'

import { createPhoto } from 'data/api/create-photo'
import { MediaUploadPhotoDto } from 'types/dtos/media-upload'

type Props = {
  storeImage: (image: MediaUploadPhotoDto) => void
  onError?: (element: string | JSX.Element) => void
}

const Upload = ({ storeImage, onError }: Props) => {
  const translate = useTranslate('conversation.composer')
  const fileInput = useRef<HTMLInputElement>(null)

  const handleClick = () => fileInput.current?.click()

  const createImage = async (file: File) => {
    const type = 'user_msg'
    const photo = new FormData()

    if (file.size > MAXIMUM_FILE_SIZE_BYTES) {
      if (onError) onError(translate('errors.upload.file_size'))

      return
    }

    photo.append('photo[type]', type)
    photo.append('photo[file]', file)

    const response = await createPhoto(photo)

    if ('errors' in response) return

    storeImage(response)
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const filesFromEvent = event.target.files

    if (!filesFromEvent) return

    const files = Array.from(filesFromEvent)

    files.forEach(createImage)
  }

  return (
    <>
      <input
        ref={fileInput}
        onChange={onChange}
        hidden
        type="file"
        accept={AcceptedFileTypes.Images}
        multiple
      />
      <Button
        inline
        styling={Button.Styling.Flat}
        theme="muted"
        icon={<Icon name={Camera24} testId="camera" color={Icon.Color.GreyscaleLevel3} />}
        onClick={handleClick}
        aria={{
          'aria-label': translate('a11y.actions.upload'),
        }}
      />
    </>
  )
}

export default Upload
